// src/AdminPortal.js
import React, { useState, useEffect } from 'react';
import { auth, googleProvider, ADMIN_EMAIL, db } from './firebase';
import { signInWithPopup, signOut } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc } from 'firebase/firestore';
import Footer from './Footer';

const NIGERIAN_STATES = [
  'All States', 'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT', 'Gombe', 'Imo', 'Jigawa',
  'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun',
  'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

const AdminPortal = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, verified: 0, pending: 0, flagged: 0 });
  const [editingStation, setEditingStation] = useState(null);
  const [selectedStations, setSelectedStations] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [filterState, setFilterState] = useState('All States');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
      setIsAdmin(currentUser?.email === ADMIN_EMAIL);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      loadStations();
    }
  }, [isAdmin]);

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (result.user.email !== ADMIN_EMAIL) {
        await signOut(auth);
        alert('Access denied. Only authorized admin can access this portal.');
      }
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Sign in failed. Please try again.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const loadStations = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'stations'));
      const stationsData = [];
      querySnapshot.forEach((doc) => {
        stationsData.push({ id: doc.id, ...doc.data() });
      });
      setStations(stationsData);
      
      // Calculate stats
      setStats({
        total: stationsData.length,
        verified: stationsData.filter(s => s.verified).length,
        pending: stationsData.filter(s => s.pendingModeration).length,
        flagged: stationsData.filter(s => s.reportCount > 0).length
      });
    } catch (error) {
      console.error('Error loading stations:', error);
    }
  };

  const handleVerifyStation = async (stationId) => {
    try {
      await updateDoc(doc(db, 'stations', stationId), {
        verified: true,
        pendingModeration: false,
        reportCount: 0
      });
      await loadStations();
      alert('Station verified successfully!');
    } catch (error) {
      console.error('Error verifying station:', error);
      alert('Failed to verify station.');
    }
  };

  const handleDeleteStation = async (stationId, stationName) => {
    if (window.confirm(`Are you sure you want to delete "${stationName}"? This cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'stations', stationId));
        await loadStations();
        alert('Station deleted successfully!');
      } catch (error) {
        console.error('Error deleting station:', error);
        alert('Failed to delete station.');
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStations.length === 0) {
      alert('Please select stations to delete');
      return;
    }

    if (window.confirm(`Delete ${selectedStations.length} selected station(s)? This cannot be undone.`)) {
      try {
        const deletePromises = selectedStations.map(id => 
          deleteDoc(doc(db, 'stations', id))
        );
        await Promise.all(deletePromises);
        setSelectedStations([]);
        await loadStations();
        alert(`Successfully deleted ${selectedStations.length} station(s)!`);
      } catch (error) {
        console.error('Error bulk deleting:', error);
        alert('Failed to delete some stations.');
      }
    }
  };

  const toggleSelectStation = (stationId) => {
    setSelectedStations(prev => 
      prev.includes(stationId) 
        ? prev.filter(id => id !== stationId)
        : [...prev, stationId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStations.length === stations.length) {
      setSelectedStations([]);
    } else {
      setSelectedStations(stations.map(s => s.id));
    }
  };

  const handleBulkUploadCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        const newStations = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(',').map(v => v.trim());
          const station = {};
          
          headers.forEach((header, index) => {
            station[header] = values[index] || '';
          });

          // Parse prices if they exist
          if (station.PMS || station.AGO || station.DPK || station.LPG) {
            station.prices = {
              PMS: parseInt(station.PMS) || 0,
              AGO: parseInt(station.AGO) || 0,
              DPK: parseInt(station.DPK) || 0,
              LPG: parseInt(station.LPG) || 0
            };
            delete station.PMS;
            delete station.AGO;
            delete station.DPK;
            delete station.LPG;
          }

          // Set defaults
          station.availability = station.availability || { PMS: 'available', AGO: 'available', DPK: 'available', LPG: 'available' };
          station.queue = station.queue || 'none';
          station.verified = station.verified === 'true' || false;
          station.lastUpdated = new Date().toISOString();
          station.lat = parseFloat(station.lat) || 0;
          station.lng = parseFloat(station.lng) || 0;
          station.ownerCode = station.ownerCode || Math.random().toString(36).substring(2, 10).toUpperCase();
          station.createdBy = 'Admin Bulk Upload';
          station.updatedBy = 'Admin';
          station.updatedByOwner = true;

          newStations.push(station);
        }

        // Upload to Firestore
        const uploadPromises = newStations.map(station =>
          addDoc(collection(db, 'stations'), station)
        );
        
        await Promise.all(uploadPromises);
        await loadStations();
        setShowBulkUpload(false);
        alert(`Successfully uploaded ${newStations.length} station(s)!`);
      } catch (error) {
        console.error('CSV upload error:', error);
        alert('Failed to upload CSV. Please check the format.');
      }
    };
    
    reader.readAsText(file);
  };

  const handleEditStation = (station) => {
    setEditingStation({...station});
  };

  const handleSaveEdit = async () => {
    try {
      const stationRef = doc(db, 'stations', editingStation.id);
      const { id, ...updateData } = editingStation;
      await updateDoc(stationRef, {
        ...updateData,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'Admin',
        updatedByOwner: true // Admin updates also marked as authoritative
      });
      await loadStations();
      setEditingStation(null);
      alert('Station updated successfully!');
    } catch (error) {
      console.error('Error updating station:', error);
      alert('Failed to update station.');
    }
  };

  const updateEditField = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditingStation({
        ...editingStation,
        [parent]: {
          ...editingStation[parent],
          [child]: value
        }
      });
    } else {
      setEditingStation({
        ...editingStation,
        [field]: value
      });
    }
  };

  const exportData = () => {
    const dataStr = JSON.stringify(stations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `naija-petrol-stations-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const exportCSV = () => {
    const headers = ['ID', 'Name', 'Address', 'City', 'State', 'Lat', 'Lng', 'PMS', 'AGO', 'DPK', 'Verified', 'Reports'];
    const rows = stations.map(s => [
      s.id,
      s.name,
      s.address,
      s.city,
      s.state,
      s.lat,
      s.lng,
      s.prices?.PMS || 0,
      s.prices?.AGO || 0,
      s.prices?.DPK || 0,
      s.verified ? 'Yes' : 'No',
      s.reportCount || 0
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `naija-petrol-stations-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-600 to-green-800">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🔐</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h1>
            <p className="text-gray-600">Naija Petrol Prices</p>
          </div>
          <button
            onClick={handleGoogleSignIn}
            className="w-full bg-white border-2 border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 flex items-center justify-center gap-3 transition-all"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
          <p className="text-xs text-gray-500 text-center mt-4">
            Only authorized admins can access this portal
          </p>
        </div>
      </div>
    );
  }

  // Filter stations by state and search query
  const filteredStations = stations.filter(station => {
    const matchesState = filterState === 'All States' || station.state === filterState;
    const matchesSearch = searchQuery === '' || 
      station.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      station.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      station.city?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesState && matchesSearch;
  });

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">⛔</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You are signed in as <strong>{user.email}</strong>, but you don't have admin privileges.
          </p>
          <button
            onClick={handleSignOut}
            className="bg-red-600 text-white py-2 px-6 rounded-lg hover:bg-red-700"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
            <p className="text-sm text-gray-600">Manage Naija Petrol Prices</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
              <p className="text-xs text-gray-600">{user.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-red-700"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Total Stations</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Verified</div>
            <div className="text-3xl font-bold text-green-600">{stats.verified}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Pending Review</div>
            <div className="text-3xl font-bold text-yellow-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600 mb-2">Flagged</div>
            <div className="text-3xl font-bold text-red-600">{stats.flagged}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* State Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by State
              </label>
              <select
                value={filterState}
                onChange={(e) => setFilterState(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {NIGERIAN_STATES.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>

            {/* Search Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Stations
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, address, or city..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Filter Results Info */}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredStations.length}</span> of <span className="font-semibold">{stations.length}</span> stations
              {filterState !== 'All States' && <span> in {filterState}</span>}
              {searchQuery && <span> matching "{searchQuery}"</span>}
            </p>
            {(filterState !== 'All States' || searchQuery) && (
              <button
                onClick={() => {
                  setFilterState('All States');
                  setSearchQuery('');
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Bulk Operations</h2>
          
          {/* Bulk Upload */}
          <div className="mb-6">
            <h3 className="font-semibold mb-2">Bulk Upload CSV</h3>
            <p className="text-sm text-gray-600 mb-3">
              Upload multiple stations at once. CSV format: name,address,city,state,lat,lng,PMS,AGO,DPK,LPG,verified
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkUpload(true)}
                className="bg-purple-600 text-white py-2 px-6 rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload CSV
              </button>
              <a
                href="data:text/csv;charset=utf-8,name,address,city,state,lat,lng,PMS,AGO,DPK,LPG,verified%0ANNPC%20Sample,Allen%20Avenue,Ikeja,Lagos,6.6018,3.3515,650,1200,950,1400,true"
                download="sample-upload.csv"
                className="bg-gray-200 text-gray-700 py-2 px-6 rounded-lg hover:bg-gray-300 flex items-center gap-2"
              >
                📄 Download Sample CSV
              </a>
            </div>
          </div>

          {/* Bulk Delete */}
          {selectedStations.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="font-semibold text-red-900 mb-2">
                {selectedStations.length} station(s) selected
              </h3>
              <button
                onClick={handleBulkDelete}
                className="bg-red-600 text-white py-2 px-6 rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Selected
              </button>
            </div>
          )}

          {/* Export */}
          <div>
            <h3 className="font-semibold mb-2">Data Export</h3>
            <div className="flex gap-4">
              <button
                onClick={exportData}
                className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                📥 Export JSON
              </button>
              <button
                onClick={exportCSV}
                className="bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                📊 Export CSV
              </button>
              <button
                onClick={loadStations}
                className="bg-gray-600 text-white py-2 px-6 rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                🔄 Refresh Data
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Upload Modal */}
        {showBulkUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-bold mb-4">Upload Stations CSV</h3>
              <p className="text-sm text-gray-600 mb-4">
                Select a CSV file with stations data. Format: name,address,city,state,lat,lng,PMS,AGO,DPK,LPG,verified
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleBulkUploadCSV}
                className="w-full border rounded-lg px-3 py-2 mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBulkUpload(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stations List */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold text-gray-900">All Stations</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedStations.length === filteredStations.length && filteredStations.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prices</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredStations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium">No stations found</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {filterState !== 'All States' || searchQuery 
                          ? 'Try adjusting your filters'
                          : 'Upload stations to get started'
                        }
                      </p>
                      {(filterState !== 'All States' || searchQuery) && (
                        <button
                          onClick={() => {
                            setFilterState('All States');
                            setSearchQuery('');
                          }}
                          className="mt-4 text-green-600 hover:text-green-700 font-medium"
                        >
                          Clear Filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredStations.map(station => (
                  <tr key={station.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedStations.includes(station.id)}
                        onChange={() => toggleSelectStation(station.id)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{station.name}</div>
                      <div className="text-xs text-gray-500">ID: {station.id}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{station.address}</div>
                      <div className="text-xs text-gray-500">{station.city}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {station.state}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div>PMS: ₦{station.prices?.PMS || 0}</div>
                      <div>AGO: ₦{station.prices?.AGO || 0}</div>
                      <div>DPK: ₦{station.prices?.DPK || 0}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {station.verified && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Verified
                        </span>
                      )}
                      {station.pendingModeration && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Pending
                        </span>
                      )}
                      {station.reportCount > 0 && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 ml-1">
                          {station.reportCount} Reports
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditStation(station)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          ✏️ Edit
                        </button>
                        {!station.verified && (
                          <button
                            onClick={() => handleVerifyStation(station.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            ✓ Verify
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteStation(station.id, station.name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Station Modal */}
        {editingStation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl font-bold mb-6">Edit Station</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Station Name</label>
                    <input
                      type="text"
                      value={editingStation.name}
                      onChange={(e) => updateEditField('name', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">State</label>
                    <input
                      type="text"
                      value={editingStation.state}
                      onChange={(e) => updateEditField('state', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                      type="text"
                      value={editingStation.city}
                      onChange={(e) => updateEditField('city', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Address</label>
                    <input
                      type="text"
                      value={editingStation.address}
                      onChange={(e) => updateEditField('address', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Latitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editingStation.lat}
                      onChange={(e) => updateEditField('lat', parseFloat(e.target.value))}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Longitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={editingStation.lng}
                      onChange={(e) => updateEditField('lng', parseFloat(e.target.value))}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Fuel Prices (₦)</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">PMS (Petrol/L)</label>
                      <input
                        type="number"
                        value={editingStation.prices?.PMS || 0}
                        onChange={(e) => updateEditField('prices.PMS', parseInt(e.target.value))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">AGO (Diesel/L)</label>
                      <input
                        type="number"
                        value={editingStation.prices?.AGO || 0}
                        onChange={(e) => updateEditField('prices.AGO', parseInt(e.target.value))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">DPK (Kerosene/L)</label>
                      <input
                        type="number"
                        value={editingStation.prices?.DPK || 0}
                        onChange={(e) => updateEditField('prices.DPK', parseInt(e.target.value))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">LPG (Gas/kg)</label>
                      <input
                        type="number"
                        value={editingStation.prices?.LPG || 0}
                        onChange={(e) => updateEditField('prices.LPG', parseInt(e.target.value))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Availability</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">PMS</label>
                      <select
                        value={editingStation.availability?.PMS || 'available'}
                        onChange={(e) => updateEditField('availability.PMS', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="available">Available</option>
                        <option value="limited">Limited</option>
                        <option value="out">Out of Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">AGO</label>
                      <select
                        value={editingStation.availability?.AGO || 'available'}
                        onChange={(e) => updateEditField('availability.AGO', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="available">Available</option>
                        <option value="limited">Limited</option>
                        <option value="out">Out of Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">DPK</label>
                      <select
                        value={editingStation.availability?.DPK || 'available'}
                        onChange={(e) => updateEditField('availability.DPK', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="available">Available</option>
                        <option value="limited">Limited</option>
                        <option value="out">Out of Stock</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">LPG</label>
                      <select
                        value={editingStation.availability?.LPG || 'available'}
                        onChange={(e) => updateEditField('availability.LPG', e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="available">Available</option>
                        <option value="limited">Limited</option>
                        <option value="out">Out of Stock</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Queue Status</label>
                    <select
                      value={editingStation.queue}
                      onChange={(e) => updateEditField('queue', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                    >
                      <option value="none">No Queue</option>
                      <option value="short">Short</option>
                      <option value="moderate">Moderate</option>
                      <option value="long">Long</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Owner Code</label>
                    <input
                      type="text"
                      value={editingStation.ownerCode || ''}
                      onChange={(e) => updateEditField('ownerCode', e.target.value)}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Generate unique code"
                    />
                    <p className="text-xs text-gray-500 mt-1">Share this with station owner to claim ownership</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingStation.verified}
                      onChange={(e) => updateEditField('verified', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Verified Station</span>
                  </label>
                </div>

                <div className="flex gap-3 mt-6 pt-6 border-t">
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditingStation(null)}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default AdminPortal;
